import { createSlice } from '@reduxjs/toolkit';

import { updateProfile, updateProfileThunk } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { setCurrentUser } from './user.duck';
import { getWishlist } from '../util/dataExtractors';
import { storableError } from '../util/errors';
import {
  getLocalFavoritesItems,
  setLocalFavoritesItems,
  toggleFavoriteId,
  isListingFavorited,
} from '../util/favoritesHelpers';

const createInitialState = () => ({
  localFavorites: getLocalFavoritesItems(),
  inProgressById: {},
  toggleFavoriteError: null,
});

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState: createInitialState,
  reducers: {
    setLocalFavorites: (state, action) => {
      state.localFavorites = action.payload;
    },
    toggleFavoriteStarted: (state, action) => {
      state.inProgressById[action.payload] = true;
      state.toggleFavoriteError = null;
    },
    toggleFavoriteFinished: (state, action) => {
      delete state.inProgressById[action.payload];
    },
    toggleFavoriteFailed: (state, action) => {
      const { listingId, error } = action.payload;
      delete state.inProgressById[listingId];
      state.toggleFavoriteError = error;
    },
  },
});

export const {
  setLocalFavorites,
  toggleFavoriteStarted,
  toggleFavoriteFinished,
  toggleFavoriteFailed,
} = favoritesSlice.actions;

export default favoritesSlice.reducer;

export const selectFavoriteIds = state => {
  const isAuthenticated = state?.auth?.isAuthenticated;
  const currentUser = state?.user?.currentUser;

  return isAuthenticated ? getWishlist(currentUser) : state?.favorites?.localFavorites || [];
};

export const selectIsFavoriteInProgress = (state, listingId) => {
  return Boolean(state?.favorites?.inProgressById?.[listingId]);
};

export const selectIsListingFavorited = (state, listingId) => {
  return isListingFavorited(selectFavoriteIds(state), listingId);
};

export const syncGuestFavorites = () => dispatch => {
  dispatch(setLocalFavorites(getLocalFavoritesItems()));
};

const withUpdatedWishlist = (currentUser, wishlist) => {
  if (!currentUser) {
    return currentUser;
  }

  const profile = currentUser?.attributes?.profile || {};
  const publicData = profile?.publicData || {};

  return {
    ...currentUser,
    attributes: {
      ...currentUser.attributes,
      profile: {
        ...profile,
        publicData: {
          ...publicData,
          wishlist,
        },
      },
    },
  };
};

export const toggleFavorite = listingId => async (dispatch, getState) => {
  if (!listingId) {
    return [];
  }

  const state = getState();
  if (selectIsFavoriteInProgress(state, listingId)) {
    return selectFavoriteIds(state);
  }

  const isAuthenticated = state?.auth?.isAuthenticated;
  const currentUser = state?.user?.currentUser;
  const currentFavorites = selectFavoriteIds(state);
  const nextFavorites = toggleFavoriteId(currentFavorites, listingId);

  dispatch(toggleFavoriteStarted(listingId));

  try {
    if (isAuthenticated) {
      const optimisticCurrentUser = withUpdatedWishlist(currentUser, nextFavorites);
      if (optimisticCurrentUser) {
        dispatch(setCurrentUser(optimisticCurrentUser));
      }

      const resultAction = await dispatch(
        updateProfile({
          publicData: {
            wishlist: nextFavorites,
          },
        })
      );

      if (resultAction?.type === updateProfileThunk.rejected.type) {
        throw resultAction.payload || new Error('Could not update favorites');
      }
    } else {
      setLocalFavoritesItems(nextFavorites);
      dispatch(setLocalFavorites(nextFavorites));
    }

    dispatch(toggleFavoriteFinished(listingId));
    return nextFavorites;
  } catch (e) {
    if (isAuthenticated && currentUser) {
      dispatch(setCurrentUser(currentUser));
    }
    const error = storableError(e);
    dispatch(toggleFavoriteFailed({ listingId, error }));
    throw error;
  }
};
